// Display helpers for run/episode documents

export const STATUS_CONFIG = {
  processing: { label: 'Processing', tone: 'orange', spinning: true },
  failed: { label: 'Failed', tone: 'red', spinning: false },
  pending: { label: 'Pending', tone: 'amber', spinning: false },
  queued: { label: 'Queued', tone: 'amber', spinning: false },
  transcribing: { label: 'Transcribing', tone: 'orange', spinning: true },
  mapping: { label: 'Creating show notes', tone: 'orange', spinning: true },
  'creating-shorts': { label: 'Creating shorts', tone: 'purple', spinning: true },
  done: { label: 'Ready', tone: 'green', spinning: false },
};

export const TONE_CLASSES = {
  amber: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/30',
    gradient: 'from-amber-500/30 to-orange-500/20',
    dot: 'bg-amber-400',
  },
  orange: {
    bg: 'bg-orange-500/15',
    text: 'text-orange-300',
    border: 'border-orange-500/30',
    gradient: 'from-orange-500/30 to-red-500/20',
    dot: 'bg-orange-400',
  },
  green: {
    bg: 'bg-green-500/15',
    text: 'text-green-300',
    border: 'border-green-500/30',
    gradient: 'from-green-500/25 to-emerald-500/15',
    dot: 'bg-green-400',
  },
  purple: {
    bg: 'bg-purple-500/15',
    text: 'text-purple-300',
    border: 'border-purple-500/30',
    gradient: 'from-purple-500/30 to-violet-500/20',
    dot: 'bg-purple-400',
  },
  red: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/30',
    gradient: 'from-red-500/30 to-rose-500/20',
    dot: 'bg-red-400',
  },
};

export function getRunStatus(run) {
  if (run.error || run.errorMessage) {
    return { key: 'error', label: 'Failed', tone: 'red', spinning: false };
  }
  const cfg = STATUS_CONFIG[run.status] || { label: run.status || 'Unknown', tone: 'amber', spinning: false };
  return { key: run.status || 'unknown', ...cfg };
}

export function getRunTitle(run) {
  return run.episodeName || run.originalFileName || 'Untitled episode';
}

export function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts._seconds != null) return new Date(ts._seconds * 1000);
  if (ts.seconds != null) return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  if (typeof ts === 'string') return new Date(ts);
  if (typeof ts === 'number') return new Date(ts);
  return null;
}

export function timeAgo(ts) {
  const d = toDate(ts);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const week = Math.floor(day / 7);
  if (week < 5) return `${week}w ago`;
  return d.toLocaleDateString();
}

export function formatDate(ts) {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getStage(run, stage) {
  const value = { ...(run[stage] || {}) };
  Object.entries(run).forEach(([key, data]) => {
    if (key.startsWith(`${stage}.`) && (!Object.prototype.hasOwnProperty.call(value, 'attemptId') || value[key.slice(stage.length + 1)] === undefined)) value[key.slice(stage.length + 1)] = data;
  });
  if (!value.status && stage === 'mapping') value.status = run.llmResponse ? 'done' : ((run.shouldCreateMapping ?? true) ? 'pending' : 'not-requested');
  if (!value.status && stage === 'shorts') value.status = value.shortResults ? 'done' : (run.shouldCreateShorts ? 'pending' : 'not-requested');
  return value;
}
export function stageBusy(stage) {
  if (stage.leaseUntil && stage.leaseUntil < Date.now()) return false;
  return ['pending', 'queued', 'processing', 'extracting', 'preparing', 'rendering', 'creating-shorts'].includes(stage.status);
}
